import React from 'react';
import { Alert, Checkbox, Table } from 'antd';
import { useTranslation } from 'react-i18next';
import { formatCurrencyGrouped, formatCurrencyUnitPrice } from '../../../utils/currency';
import { formatUnit } from '../../../utils/units';
import { formatDateEuropean } from '../../../utils/dateUtils';
import type { InvoiceApprovalPreview } from '../api/invoiceWorkflowApi';

type Alignment = NonNullable<InvoiceApprovalPreview['purchaseAlignment'] | InvoiceApprovalPreview['salesAlignment']>;

export default function InvoiceOrderAlignment({ value, scope, accepted, disabled, onAccept }: {
  value: Alignment;
  scope: 'purchase' | 'sales';
  accepted: boolean;
  disabled: boolean;
  onAccept: (accepted: boolean) => void;
}) {
  const { t } = useTranslation('invoices');
  const text = (key: string) => t(`workflow.alignment.${key}`);
  if (!value.changes.length) return null;
  const display = (field: string, raw: string | number | null, currency?: string) => {
    if (raw == null) return '—';
    if (['exchangeRateSource', 'exchangeRateToPLNSource'].includes(field)) {
      const sources = ['NBP', 'NBP_HISTORICAL', 'MANUAL', 'OPERATOR_CONFIRMED', 'PLN', 'DATABASE_CACHE', 'FALLBACK', 'LEGACY_SAVED_RATE'];
      return text(`sources.${sources.includes(String(raw)) ? raw : 'unknown'}`);
    }
    if (['exchangeRateRequestedDate', 'exchangeRateEffectiveDate', 'exchangeRateToPLNRequestedDate', 'exchangeRateToPLNEffectiveDate', 'invoiceDate'].includes(field)) {
      // These are saved calendar dates, including legacy UTC JSON timestamps.
      return typeof raw === 'string' ? formatDateEuropean(raw.slice(0, 10)) : '—';
    }
    if (['unit', 'purchaseUnit', 'saleUnit'].includes(field)) return formatUnit(String(raw));
    if (typeof raw === 'number' && [
      'totalAmount', 'totalAmountPLN', 'unitCost', 'totalCost', 'totalCostPLN',
      'unitPrice', 'totalPrice', 'revenue',
    ].includes(field)) {
      const unit = field.endsWith('PLN') || field === 'revenue' ? 'PLN' : currency;
      if (unit) return ['unitCost', 'unitPrice'].includes(field)
        ? formatCurrencyUnitPrice(raw, unit)
        : formatCurrencyGrouped(raw, unit);
    }
    return typeof raw === 'number' ? raw.toLocaleString('pl-PL', { maximumFractionDigits: 4 }) : raw;
  };
  return <section aria-label={text(`${scope}Title`)}>
    <Alert type="warning" showIcon message={text(`${scope}Title`)} description={text(`${scope}Hint`)} />
    <Table pagination={false} size="small" rowKey={row => `${row.sourceLineId || 'header'}-${row.field}`} dataSource={value.changes} columns={[
      { title: text('field'), render: (_, row) => <>{row.productName && <div>{row.productName}</div>}{text(`fields.${row.field}`)}</> },
      { title: text(`${scope}Before`), render: (_, row) => display(row.field, row.before, row.beforeCurrency) },
      { title: text('after'), render: (_, row) => display(row.field, row.after, row.afterCurrency) },
    ]} />
    <Checkbox checked={accepted} disabled={disabled} onChange={event => onAccept(event.target.checked)}>{text(`${scope}Accept`)}</Checkbox>
  </section>;
}
